// Libraries
import { FormGroup, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import * as lodash from 'lodash';

/**
 * Provides a set of validators used by form controls.
 *
 * A validator is a function that processes a {@link FormControl} or collection of
 * controls and returns a map of errors. A null map means that validation has passed.
 *
 * ### Example
 *
 * ```typescript
 * var loginControl = new FormControl("", Validators.equalsTo)
 * ```
 */
export class WoopaValidators {
  /**
   * Validator that compares the value of the given FormControls
   */
  public static equalsTo(fieldPaths: string[]): ValidatorFn {
    const resp = (control: AbstractControl): {[key: string]: any} | null => {
      // Check if field paths is empty, throw error
      if (fieldPaths.length < 1) {
        throw new Error('You must compare to at least 1 other field');
      }
      // Check if control has parent
      if (!control.parent) {
        return null;
      }
      // For each field check if has the same value
      for (const fieldName of fieldPaths) {
        const field = (control.parent as FormGroup).get(fieldName);
        if (!field) {
          throw new Error(
              `Field: ${fieldName} undefined, are you sure that ${fieldName} exists in the group`);
        }
        // Check field and control values
        if (field.value !== control.value) {
          return { equalsTo: { unequalField: fieldName } };
        }
      }
      return null;
    };
    return resp;
  }

  /**
   * Validator that requires controls to maximun value.
   */
  public static maxValue(max: number): ValidatorFn {
    const resp = (control: AbstractControl): {[key: string]: any} | null => {
      const input = control.value;
      const error = input > max;
      return error ? { maxValue: true } : null;
    };
    return resp;
  }

  /**
   * Validate value ocurrences on control value.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @param count Value ocurrences
   * @param value Value to search
   * @returns
   * @memberof WoopaValidators
   */
  public static almost(count: number, value: any): ValidatorFn {
    const resp = (control: AbstractControl): {[key: string]: any} | null => {
      const list = control.value;
      const ocurrences = lodash.countBy(list, lodash.identity);
      const error = !ocurrences[value] || ocurrences[value] < count;
      return error ? { almost: true } : null;
    };
    return resp;
  }

  /**
   * Validate control value size. Only for arrays
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @param count Number of ocurrences to check
   * @param count Filters for list
   * @returns
   * @memberof WoopaValidators
   */
  public static almostArray(count: number, filters?: any): ValidatorFn {
    const resp = (control: AbstractControl): {[key: string]: any} | null => {
      const list = control.value;
      const newList = filters ? lodash.reject(list, filters) : list;
      const error = !lodash.isArray(newList) || newList.length < count;
      return error ? { almostArray: true } : null;
    };
    return resp;
  }
}
